import { Alert, Button, Pane, Spinner, Text, toaster } from 'evergreen-ui';
import { Component } from 'react';
import Request from '../../../util/Api';
import CardPicker from '../../cards/components/CardPicker';
import ProductPicker from '../../products/components/ProductPicker';
import ConsumablesTable from './ConsumablesTable';

class EventConsumablesTab extends Component {
  constructor(props) {
    super(props);

    this.state = {
      consumables: props.consumables || [],
      loading: false,
      saving: false,
      isEditing: false,
      originalConsumables: JSON.parse(JSON.stringify(props.consumables || []))
    };
  }

  componentDidMount() {
    // Only load consumables if not provided via props
    if (this.props.eventId && !this.props.consumables) {
      this.loadConsumables();
    }
  }

  componentDidUpdate(prevProps) {
    // Update state if consumables prop changes
    if (this.props.consumables && this.props.consumables !== prevProps.consumables) {
      this.setState({
        consumables: this.props.consumables,
        originalConsumables: JSON.parse(JSON.stringify(this.props.consumables)),
        isEditing: false
      });
    }
    // Only load consumables if eventId changes and no consumables provided via props
    else if (this.props.eventId && this.props.eventId !== prevProps.eventId && !this.props.consumables) {
      this.loadConsumables();
    }
  }

  async loadConsumables() {
    if (!this.props.eventId) return;

    this.setState({ loading: true });
    try {
      const response = await Request.get(`/events/${this.props.eventId}/consumables`);
      this.setState({
        consumables: response.data || [],
        originalConsumables: JSON.parse(JSON.stringify(response.data || [])),
        loading: false,
        isEditing: false
      });
    } catch (error) {
      console.error('Failed to load consumables:', error);
      toaster.danger('Failed to load consumables');
      this.setState({ loading: false });
    }
  }

  handleAddProducts = (selection) => {
    const newConsumables = selection.map(product => ({
      itemType: 'product',
      itemId: product.id,
      quantity: 1,
      item: {
        id: product.id,
        title: product.title || product.name,
        sku: product.sku,
        thumbnail: product.thumbnail
      }
    }));

    // Filter out duplicates
    const existingProductIds = new Set(
      this.state.consumables
        .filter(c => c.itemType === 'product')
        .map(c => c.itemId)
    );

    const uniqueNew = newConsumables.filter(c => !existingProductIds.has(c.itemId));

    this.setState(prevState => ({
      consumables: [...prevState.consumables, ...uniqueNew]
    }));
  }

  handleAddCards = (selection) => {
    const newConsumables = selection.map(card => ({
      itemType: 'single',
      itemId: card.id,
      quantity: 1,
      item: {
        id: card.id,
        name: card.name,
        image: card.image,
        expansion: card.expansion
      }
    }));

    // Filter out duplicates
    const existingSingleIds = new Set(
      this.state.consumables
        .filter(c => c.itemType === 'single')
        .map(c => c.itemId)
    );

    const uniqueNew = newConsumables.filter(c => !existingSingleIds.has(c.itemId));

    this.setState(prevState => ({
      consumables: [...prevState.consumables, ...uniqueNew]
    }));
  }

  handleQuantityChange = (index, value) => {
    const quantity = parseInt(value) || 1;
    this.setState(prevState => {
      const consumables = [...prevState.consumables];
      consumables[index] = { ...consumables[index], quantity: Math.max(1, quantity) };
      return { consumables };
    });
  }

  handleRemove = (index) => {
    this.setState(prevState => ({
      consumables: prevState.consumables.filter((_, i) => i !== index)
    }));
  }

  handleEdit = () => {
    this.setState({
      isEditing: true,
      originalConsumables: JSON.parse(JSON.stringify(this.state.consumables))
    });
  }

  handleCancelEdit = () => {
    this.setState(prevState => ({
      isEditing: false,
      consumables: JSON.parse(JSON.stringify(prevState.originalConsumables))
    }));
  }

  handleSave = async () => {
    if (!this.props.eventId) {
      toaster.warning('Please save the event first before adding consumables');
      return;
    }

    this.setState({ saving: true });
    try {
      const consumablesData = this.state.consumables.map(c => ({
        id: c.id,
        itemType: c.itemType,
        itemId: c.itemId,
        quantity: c.quantity
      }));

      const response = await Request.put(`/events/${this.props.eventId}/consumables`, {}, {}, {
        consumables: consumablesData
      });

      this.setState({
        consumables: response.data || [],
        originalConsumables: JSON.parse(JSON.stringify(response.data || [])),
        saving: false,
        isEditing: false
      });
      toaster.success('Consumables saved successfully');
    } catch (error) {
      console.error('Failed to save consumables:', error);
      toaster.danger('Failed to save consumables');
      this.setState({ saving: false });
    }
  }

  render() {
    const { consumables, loading, saving, isEditing } = this.state;
    const { disabled, eventId, isDuplicating, isFinalized } = this.props;

    if (!eventId || isDuplicating) {
      return (
        <Pane padding={24} display="flex" alignItems="center" justifyContent="center">
          <Text color="muted">Please save the event first before managing consumables.</Text>
        </Pane>
      );
    }

    if (loading) {
      return (
        <Pane padding={24} display="flex" alignItems="center" justifyContent="center">
          <Spinner size={24} marginRight={8} />
          <Text>Loading consumables...</Text>
        </Pane>
      );
    }

    return (
      <Pane>
        {/* Finalized Notice */}
        {isFinalized && (
          <Alert intent="info" marginBottom={16}>
            This event has been finalized. Consumables can no longer be edited.
          </Alert>
        )}

        {/* Actions */}
        <Pane display="flex" gap={8} marginBottom={16} justifyContent="space-between">
          <Pane display="flex" gap={8}>
            {isEditing && !isFinalized && (
              <>
                <ProductPicker minimal onSubmit={this.handleAddProducts} />
                <CardPicker minimal onSubmit={this.handleAddCards} />
              </>
            )}
          </Pane>
          <Pane display="flex" gap={8}>
            {!isEditing && !isFinalized ? (
              <Button
                iconBefore="edit"
                onClick={this.handleEdit}
                disabled={disabled || saving || isFinalized}
              >
                Edit Consumables
              </Button>
            ) : !isFinalized && (
              <>
                <Button
                  onClick={this.handleCancelEdit}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  onClick={this.handleSave}
                  isLoading={saving}
                  disabled={disabled || saving}
                >
                  Save Changes
                </Button>
              </>
            )}
          </Pane>
        </Pane>

        {/* Consumables Table */}
        <ConsumablesTable
          consumables={consumables}
          isEditing={isEditing && !isFinalized}
          onQuantityChange={this.handleQuantityChange}
          onRemove={this.handleRemove}
        />
      </Pane>
    );
  }
}

export default EventConsumablesTab;
